export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string | undefined,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
  }

  /** Field errors from a 400 VALIDATION_ERROR, keyed by path (e.g. `name`, `details.ipAddress`). */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    if (Array.isArray(this.details)) {
      for (const d of this.details as Array<{ path?: string; message?: string }>) {
        if (d.path && d.message && !out[d.path]) out[d.path] = d.message;
      }
    }
    return out;
  }
}

/** Same-origin JSON fetch with the session cookie. Throws ApiError with the server's message. */
export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      accept: 'application/json',
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...init.headers,
    },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as {
      code?: string;
      message?: string | string[];
      details?: unknown;
    } | null;
    const message = Array.isArray(body?.message) ? body.message.join(', ') : body?.message;
    throw new ApiError(
      res.status,
      body?.code,
      message ?? `Permintaan gagal (HTTP ${res.status}).`,
      body?.details,
    );
  }
  return (await res.json()) as T;
}

export const send = <T>(method: 'POST' | 'PUT' | 'PATCH' | 'DELETE', path: string, body?: unknown) =>
  api<T>(path, { method, body: body === undefined ? undefined : JSON.stringify(body) });

export function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params))
    if (v !== undefined && v !== null && v !== '' && v !== false) search.set(k, String(v));
  const s = search.toString();
  return s ? `?${s}` : '';
}

export const nf = new Intl.NumberFormat('id-ID');
export const nf1 = new Intl.NumberFormat('id-ID', { maximumFractionDigits: 1 });
export const rupiah = new Intl.NumberFormat('id-ID', {
  style: 'currency',
  currency: 'IDR',
  maximumFractionDigits: 0,
});
export const dateTime = new Intl.DateTimeFormat('id-ID', {
  dateStyle: 'medium',
  timeStyle: 'short',
  timeZone: 'Asia/Jakarta',
});

export function metres(m: number | null | undefined): string {
  if (m === null || m === undefined) return '—';
  return m >= 1000 ? `${nf1.format(m / 1000)} km` : `${nf.format(Math.round(m))} m`;
}
